import type { Message } from '../../../api/types';
import type { BookingKind } from './dispatchLink';

/**
 * The booking a thread's booking note names, read off the note itself.
 *
 * The service writes it beside the private note it posts for every booking
 * (controller/conversationBookingCont.js), in the shape the note has carried
 * since the AI draft shipped: `{ draft: true, draftId }` or
 * `{ draft: false, dispatchJobId, isQuote }`. Read here rather than parsed out
 * of the words, because the words carry dispatch's confirmation number where it
 * has one and the dashboard is addressed by the id.
 */
export interface NoteBooking {
  kind: BookingKind;
  id: string;
}

export function noteBooking(message: Pick<Message, 'contentAttributes'>): NoteBooking | null {
  const raw = message.contentAttributes?.booking;
  if (!raw || typeof raw !== 'object') return null;
  const booking = raw as Record<string, unknown>;
  const id = String((booking.draft === true ? booking.draftId : booking.dispatchJobId) ?? '').trim();
  if (!id) return null;
  return {
    kind: booking.draft === true ? 'draft' : booking.isQuote === true ? 'quote' : 'job',
    id,
  };
}

/**
 * The note's words split around the reference they print — "Reservation
 * created from this thread: Ref #48213" — so the reference itself can be the
 * link, "Draft Ref" and all. A note from before references were written that
 * way ("Dispatch job #48213 created from this thread") still links its bare
 * "#48213". `ref` is null when the words carry no reference (a note written by
 * hand, or one the service someday words differently), and the note then reads
 * as it always did.
 */
export function splitAtReference(content: string): { before: string; ref: string | null; after: string } {
  const match = /\b(?:Draft )?Ref #[A-Za-z0-9][\w-]*|#[A-Za-z0-9][\w-]*/.exec(content);
  if (!match) return { before: content, ref: null, after: '' };
  return {
    before: content.slice(0, match.index),
    ref: match[0],
    after: content.slice(match.index + match[0].length),
  };
}
